<template>
  <div id="videoRoot" :class="playerRootClass">
    <div id="lvpsPlayerLoading" class="lvps-player-loading">
      <img src="https://lvps.6oolab.com/tail-spin.svg" />
    </div>
    <video
      id="video"
      :src="source"
      :crossorigin="crossorigin"
      preload="metadata"
      controls
      playsinline
    ></video>
  </div>
</template>

<script>
import Plyr from 'plyr'
import Hls from 'hls.js'
import axios from 'axios'
import parsers from 'playlist-parser'
import ga from '../lib/ga'
import platform from 'platform'

const LVPS_ID = window.LVPS_ID
const isMobile = () => {
  return ['iOS', 'Android'].includes(platform.os.family)
}
/*
const isIOS = () => {
  return platform.os.family === 'iOS'
}
*/

/*
const AD_TAG_URL =
  '//ima3vpaid.appspot.com/?adTagUrl=http%3A%2F%2Fgoogleads.g.doubleclick.net%2Fpagead%2Fads%3Fad_type%3Dvideo%26client%3Dca-video-pub-4968145218643279%26videoad_start_delay%3D0%26description_url=http%3A%2F%2Fwww.google.com%26hl%3Den%26max_ad_duration%3D40000%26adtest%3Don&type=js'
*/

// const AD_TAG_URL =
// 'https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/124319096/external/single_ad_samples&ciu_szs=300x250&impl=s&gdfp_req=1&env=vp&output=vast&unviewed_position_start=1&cust_params=deployment%3Ddevsite%26sample_ct%3Dlinear&correlator='

export default {
  data() {
    const videoId = 'video'
    const nocookie = window.location.search.includes('nocookie')

    return {
      videoId,
      $video: null,
      source: window.LVPS_MOVIE_URL,
      isLive: window.LVPS_IS_LIVE === 1,
      adEnabled: window.LVPS_AD_ENABLED === 1,
      movieAdEnabled: window.LVPS_MOVIE_AD_ENABLED === 1,
      hls: null,
      levels: [],
      hlsIsSupported: false,
      currentTime: 0,
      nocookie,
      crossorigin: nocookie ? '' : 'use-credentials'
    }
  },

  computed: {
    playerRootClass() {
      return {
        'is-live': this.$data.isLive
      }
    },
    levelHeights() {
      return this.$data.levels
        .map(level => level.height)
        .sort((a, b) => (a < b ? 1 : -1))
    },

    plyrControlsOpts() {
      const opts = this.$data.isLive
        ? ['play-large', 'play', 'mute', 'volume', 'settings']
        : [
            'play-large',
            'play',
            'progress',
            'current-time',
            'mute',
            'volume',
            'settings'
          ]

      if (!this.$data.adEnabled) {
        opts.push('pip')
      }

      opts.push('fullscreen')

      return opts
    }
  },

  mounted() {
    window.addEventListener('DOMContentLoaded', () => {
      this.initialize()
    })
  },

  methods: {
    getLevelIndexByHeight(height) {
      return this.$data.levels.map(level => level.height).indexOf(height)
    },

    getFileByHeight(height) {
      return this.levels.filter(level => level.height === height)[0].file
    },

    analyzeM3U() {
      const source = this.$data.source
      const M3U = parsers.M3U
      const axiosOpts = {
        withCredentials: !window.location.search.includes('nocookie')
      }

      return axios.get(source, axiosOpts).then(response => {
        const playList = M3U.parse(response.data)
        const tmp = source.split('/')
        tmp.pop()
        const fileBase = tmp.join('/')
        const results = []

        playList.forEach(item => {
          if (!item) {
            return
          }

          const { title, file } = item
          let height

          title.split(',').forEach(chunk => {
            if (chunk.includes('RESOLUTION')) {
              height = parseInt(chunk.split('x')[1])
            }
          })

          if (height) {
            const filePath = file.match(/^http/) ? file : `${fileBase}/${file}`

            results.push({ height, file: filePath })
          }
        })

        return results.sort((a, b) => a.height < b.height)
      })
    },

    async initialize() {
      this.$data.hlsIsSupported = Hls.isSupported()
      this.$data.$video = document.getElementById(this.$data.videoId)
      this.attachVideoEvent()

      if (this.$data.hlsIsSupported) {
        await this.initializeHls()
        this.initializePlyr()
      } else {
        this.$data.$video.addEventListener('loadedmetadata', () => {
          this.$data.$video.currentTime = this.$data.currentTime
        })
        this.$data.levels = await this.analyzeM3U()
        this.initializePlyr()
      }
    },

    initializeHls() {
      return new Promise(resolve => {
        const nocookie = this.$data.nocookie

        this.$data.hls = new Hls({
          xhrSetup(xhr) {
            xhr.withCredentials = !nocookie
          }
        })
        this.$data.hls.loadSource(this.$data.source)
        this.$data.hls.attachMedia(this.$data.$video)

        this.$data.$video.addEventListener('loadedmetadata', () => {
          this.$data.levels = this.$data.hls.levels.map((level, index) => {
            level.index = index
            return level
          })
          resolve()
        })
      })
    },

    async initializePlyr() {
      const self = this
      const defaultQuality = isMobile() ? 360 : 720
      const options = {
        settings: ['quality'],
        controls: this.plyrControlsOpts,
        i18n: {
          quality: '画質',
          qualityLabel: {
            360: '節約モード',
            720: '標準画質',
            1080: '高画質'
          }
        },
        ads: {
          enabled: this.movieAdEnabled,
          tagUrl: window.LVPS_AD_TAG_URL
        },
        quality: {
          default: defaultQuality,
          options: this.levelHeights,
          forced: true,
          onChange: this.handleChangeLevel.bind(self)
        },
        clickToPlay: !this.$data.isLive // Liveモードでは停止させない
      }
      let bodyBGColor = document.body.style.backgroundColor

      if (this.adEnabled) {
        options.fullscreen = {
          container: '.player-container'
        }
      }

      window.plyr = new Plyr(this.$data.$video, options)

      window.plyr.on('enterfullscreen', () => {
        document.body.style.backgroundColor = 'black'
      })

      window.plyr.on('exitfullscreen', () => {
        document.body.style.backgroundColor = bodyBGColor
      })
    },

    handleChangeLevel(height) {
      if (this.hlsIsSupported) {
        const index = this.getLevelIndexByHeight(height)
        this.hls.currentLevel = index
      } else {
        const file = this.getFileByHeight(height)
        this.$data.currentTime = this.$data.$video.currentTime
        this.$data.source = file
      }
    },

    attachVideoEvent() {
      const $video = this.$data.$video
      let played = false

      $video.addEventListener('play', () => {
        if (!played) {
          ga.sendPlay(LVPS_ID, 1)
          played = true

          const DURATION_INTERVAL = 10 // sec
          setInterval(() => {
            if (!$video.paused) {
              ga.sendDuration(LVPS_ID, DURATION_INTERVAL)
            }
          }, DURATION_INTERVAL * 1000)
        }
      })

      $video.addEventListener('ended', () => {
        if (!this.isLive) {
          window.App.video.after()
        }
      })

      $video.addEventListener('canplay', () => {
        this.$emit('canplay')
      })
      $video.load()
    }
  }
}
</script>

<style lang="sass">
#videoRoot.is-live
  button[aria-label="Pause"]
    display: none

#videoRoot
  position: relative
  .plyr-ads
    width: 100%
    height: 100%
    z-index: 100
  #adContainer
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    z-index: 1000

.lvps-player-loading
  display: none
  position: absolute
  width: 100%
  height: 100%
  top: 0
  left: 0
  z-index: 500
  justify-content: center
  background-color: rgba(0,0,0,0.8)
</style>



// WEBPACK FOOTER //
// src/components/Player.vue